import React from "react";
import {
  Badge,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { DownloadIcon } from "@chakra-ui/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CrudPage } from "../components/CrudPage";
import { PaymentBatchCardForm } from "../components/forms/PaymentBatchCardForm";
import { paymentBatchCards } from "../services/api";
import { useAuth } from "../context/AuthContext";

const pageSize = 20;

const formatRial = (value?: number) =>
  value ? `${Number(value).toLocaleString("fa-IR")} ریال` : "—";

const formatDate = (value?: string) =>
  value ? new Date(value).toLocaleDateString("fa-IR") : "—";

const statusMeta: Record<string, { label: string; color: string }> = {
  active: { label: "فعال", color: "green" },
  partially_used: { label: "بخشی استفاده شده", color: "yellow" },
  used: { label: "استفاده شده", color: "blue" },
  cancelled: { label: "لغو شده", color: "red" },
};

const createModeMeta: Record<string, string> = {
  single: "تکی",
  batch: "بسته‌ای",
};

export const PaymentBatchCardsPage: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const [itemToDelete, setItemToDelete] = React.useState<any>(null);
  const createModal = useDisclosure();
  const deleteModal = useDisclosure();

  const createMutation = useMutation(paymentBatchCards.create, {
    onSuccess: () => {
      queryClient.invalidateQueries(["paymentBatchCards"]);
      queryClient.invalidateQueries(["paymentCards"]);
      createModal.onClose();
      toast({
        title: "بسته کارت با موفقیت ایجاد شد",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    },
    onError: (error: any) => {
      toast({
        title: "خطا در ایجاد بسته کارت",
        description:
          error?.response?.data?.message || error?.message || "خطای ناشناخته",
        status: "error",
        duration: 3500,
        isClosable: true,
      });
    },
  });

  const deleteMutation = useMutation(paymentBatchCards.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries(["paymentBatchCards"]);
      queryClient.invalidateQueries(["paymentCards"]);
      deleteModal.onClose();
      toast({
        title: "بسته کارت با موفقیت لغو شد",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    },
    onError: (error: any) => {
      toast({
        title: "خطا در لغو بسته کارت",
        description:
          error?.response?.data?.message || error?.message || "خطای ناشناخته",
        status: "error",
        duration: 3500,
        isClosable: true,
      });
    },
  });

  const columns = [
    { header: "عنوان", accessor: "title" },
    {
      header: "نوع ایجاد",
      accessor: "createMode",
      render: (item: any) => createModeMeta[item.createMode] || "—",
    },
    {
      header: "تعداد",
      accessor: "quantity",
      render: (item: any) => Number(item.quantity || 0).toLocaleString("fa-IR"),
    },
    {
      header: "مبلغ هر کارت",
      accessor: "amount",
      render: (item: any) => formatRial(item.amount),
    },
    {
      header: "مبلغ کل",
      accessor: "totalAmount",
      render: (item: any) => formatRial(item.totalAmount),
    },
    {
      header: "وضعیت",
      accessor: "status",
      render: (item: any) => {
        const meta = statusMeta[item.status] || {
          label: item.status || "—",
          color: "gray",
        };
        return <Badge colorScheme={meta.color}>{meta.label}</Badge>;
      },
    },
    {
      header: "آمار کارت‌ها",
      accessor: "stats",
      render: (item: any) =>
        `فعال ${Number(item.stats?.activeCount || 0).toLocaleString("fa-IR")} / استفاده‌شده ${Number(item.stats?.usedCount || 0).toLocaleString("fa-IR")}`,
    },
    {
      header: "انقضا",
      accessor: "expiresAt",
      render: (item: any) => formatDate(item.expiresAt),
    },
    {
      header: "خروجی",
      accessor: "export",
      render: (item: any) => (
        <Button
          size="sm"
          variant="outline"
          leftIcon={<DownloadIcon />}
          onClick={() => handleExport(item)}
        >
          اکسل
        </Button>
      ),
      excludeFromExport: true,
    },
  ];

  const handleExport = async (item: any) => {
    try {
      const response = await paymentBatchCards.export(item._id);
      const fileBlob = new Blob([response.data], {
        type: "text/csv;charset=utf-8;",
      });
      const blobUrl = window.URL.createObjectURL(fileBlob);
      const link = document.createElement("a");
      const disposition = String(response.headers["content-disposition"] || "");
      const filenameMatch = disposition.match(/filename="(.+)"/);
      link.href = blobUrl;
      link.download = filenameMatch?.[1] || `payment-batch-${item._id}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error: any) {
      toast({
        title: "خطا در دریافت فایل اکسل",
        description:
          error?.response?.data?.message || error?.message || "خطای ناشناخته",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      <CrudPage
        title="بسته‌های کارت هدیه"
        columns={columns}
        queryKey="paymentBatchCards"
        api={paymentBatchCards}
        FormComponent={() => null}
        resourceName="paymentBatchCards"
        enableView={false}
        enableEdit={false}
        enableDelete={false}
        pageSize={pageSize}
        keepPreviousData
        customAddAction={
          hasPermission("paymentBatchCards:create")
            ? () => createModal.onOpen()
            : undefined
        }
        customDeleteAction={
          hasPermission("paymentBatchCards:delete")
            ? (item) => {
                setItemToDelete(item);
                deleteModal.onOpen();
              }
            : undefined
        }
      />

      <Modal
        isOpen={createModal.isOpen}
        onClose={createModal.onClose}
        size="xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>ایجاد بسته کارت هدیه</ModalHeader>
          <ModalCloseButton left={4} right="auto" aria-label="بستن" />
          <ModalBody py={4}>
            <PaymentBatchCardForm
              mode="batch"
              onSubmit={(formData) => createMutation.mutate(formData)}
              onCancel={createModal.onClose}
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.onClose}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>لغو بسته کارت</ModalHeader>
          <ModalCloseButton left={4} right="auto" aria-label="بستن" />
          <ModalBody pb={6}>
            <VStack align="stretch" spacing={4}>
              <div>
                با لغو این بسته، همه کارت‌های فعال آن لغو می‌شوند. کارت‌های
                استفاده‌شده حفظ خواهند شد.
              </div>
              <Button
                colorScheme="red"
                onClick={() =>
                  itemToDelete?._id && deleteMutation.mutate(itemToDelete._id)
                }
                isLoading={deleteMutation.isLoading}
              >
                تایید لغو
              </Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};
